import React, { forwardRef } from "react";
import html2canvas from "html2canvas";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { toFixed, format_date } from "./CustomComponent";
import defaultLogo from "../assets/images/logo512.png";

// pdfmake 0.3.x export vfs_fonts เป็น vfs object ตรง ๆ (ตาม pattern ใน CompanyDocuments)
pdfMake.vfs = pdfFonts;

// สลิปเงินเดือน (PaySlip) — เปลี่ยน logo และข้อมูลเป็นของบริษัทจาก props.company
const labelStyle = { color: "#5a6b7b", fontSize: 12 };
const cellPad = "7px 12px";

const PayslipDocument = forwardRef(({ company, payroll, item }, ref) => {
  const emp = item?.employee || {};
  const empName = item?.employee_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "-";
  const empCode = emp.employee_code || "";
  const bank = emp.bank_name ? `${emp.bank_name} ${emp.bank_account_no || ""}` : "-";
  const monthLabel = (payroll?.period_month || "").replace("-", "/");
  const companyName = company?.name_th || company?.name_en || "บริษัทของคุณ";
  const taxId = company?.tax_number ? ` (Tax ID : ${company.tax_number})` : "";
  const logo = defaultLogo;

  const incomeRows = [
    { label: "เงินเดือน", value: item?.salary },
    { label: "ค่าล่วงเวลา", value: item?.overtime },
    { label: "ค่าชดเชย/โบนัส", value: item?.bonus },
  ];
  const deductionRows = [
    { label: "ภาษีหัก ณ ที่จ่าย", value: item?.wht },
    { label: "ประกันสังคม", value: item?.sso },
    { label: "อื่นๆ", value: item?.other },
  ];

  return (
    <div
      ref={ref}
      style={{
        width: 760,
        background: "#fff",
        color: "#1b2a3a",
        fontFamily: "'Sarabun', 'Tahoma', sans-serif",
        border: "1px solid #e3e9ef",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, padding: "18px 22px", borderBottom: "2px solid #1f5fbf" }}>
        {/* โลโก้อยู่คอลัมน์ซ้าย ชื่อบริษัทกับข้อมูลพนักงานเรียงลงในคอลัมน์ขวาของโลโก้
            (เดิมใช้ marginTop ติดลบดันขึ้น พอชื่อบริษัทยาวจนขึ้น 2 บรรทัดจะทับข้อมูลพนักงาน) */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, maxWidth: 520, minWidth: 0 }}>
          <img src={logo} alt="logo" style={{ height: 50, width: 50, objectFit: "contain", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#1f5fbf", lineHeight: 1.35 }}>
              {companyName}{taxId}
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.75, marginTop: 8 }}>
              <b>ข้อมูลพนักงาน</b><br />
              ชื่อ-นามสกุล (รหัส) : {empName} {empCode ? `(${empCode})` : ""}<br />
              แผนก : {emp.department || "-"}<br />
              ตำแหน่ง : {item?.position || "-"}<br />
              รับเงินโดย : {bank}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: 12, lineHeight: 1.75, flexShrink: 0, paddingLeft: 16, whiteSpace: "nowrap" }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>สลิปเงินเดือน / PaySlip</div>
          <div style={{ marginTop: 6, fontWeight: 600 }}>รอบเงินเดือน</div>
          ตั้งแต่วันที่ : {payroll?.period_start ? format_date(payroll.period_start, "dd/mm/yyyy") : "-"}<br />
          ถึงวันที่ : {payroll?.period_end ? format_date(payroll.period_end, "dd/mm/yyyy") : "-"}<br />
          วันที่จ่ายเงินเดือน : {payroll?.pay_date ? format_date(payroll.pay_date, "dd/mm/yyyy") : "-"}
        </div>
      </div>

      {/* รายการเงินเดือน */}
      <div style={{ padding: "18px 22px" }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>รายการเงินเดือน : {monthLabel}</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, border: "1px solid #e9eef4" }}>
          <thead>
            <tr style={{ background: "#eef4fc" }}>
              <th style={{ padding: cellPad, textAlign: "left", ...labelStyle }}>รายได้</th>
              <th style={{ padding: cellPad, textAlign: "right", ...labelStyle }}>จำนวนเงิน (บาท)</th>
              <th style={{ padding: cellPad, textAlign: "left", ...labelStyle }}>รายการหัก</th>
              <th style={{ padding: cellPad, textAlign: "right", ...labelStyle }}>จำนวนเงิน (บาท)</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((i) => (
              <tr key={i}>
                <td style={{ padding: cellPad, color: "#33475b" }}>{incomeRows[i].label}</td>
                <td style={{ padding: cellPad, textAlign: "right" }}>{toFixed(incomeRows[i].value)}</td>
                <td style={{ padding: cellPad, color: "#33475b" }}>{deductionRows[i].label}</td>
                <td style={{ padding: cellPad, textAlign: "right" }}>{toFixed(deductionRows[i].value)}</td>
              </tr>
            ))}
            <tr style={{ background: "#f3f7fc", fontWeight: 700 }}>
              <td style={{ padding: cellPad }}>รายได้รวม</td>
              <td style={{ padding: cellPad, textAlign: "right" }}>{toFixed(item?.earnings_total)}</td>
              <td style={{ padding: cellPad }}>รายการหัก</td>
              <td style={{ padding: cellPad, textAlign: "right" }}>{toFixed(item?.deductions_total)}</td>
            </tr>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={3} style={{ padding: cellPad, textAlign: "right", borderTop: "1px solid #e3e9ef" }}>เงินรับสุทธิ</td>
              <td style={{ padding: cellPad, textAlign: "right", borderTop: "1px solid #e3e9ef", color: "#1f5fbf" }}>{toFixed(item?.net_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});

PayslipDocument.displayName = "PayslipDocument";

// แปลง DOM ของสลิป → pdfmake doc (ฝังรูปจาก html2canvas เลี่ยงปัญหาฟอนต์ไทย)
const buildPdfFromNode = async (node) => {
  const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  const imgData = canvas.toDataURL("image/png");
  const pageW = 595.28; // A4 width (pt)
  const imgH = (canvas.height * pageW) / canvas.width;
  return pdfMake.createPdf({
    pageSize: { width: pageW, height: imgH },
    pageMargins: [0, 0, 0, 0],
    content: [{ image: imgData, width: pageW, height: imgH }],
  });
};

// โหลดเป็นไฟล์ (ปุ่มโหลด PDF)
export const downloadPayslipPdf = async (node, filename = "payslip.pdf") => {
  const pdf = await buildPdfFromNode(node);
  pdf.download(filename);
};

// คืน blob ของ PDF ใช้ตอนรวมสลิปหลายคนเป็นไฟล์ zip
export const getPayslipPdfBlob = async (node) => {
  const pdf = await buildPdfFromNode(node);
  return pdf.getBlob();
};

// คืน base64 ของ PDF — รูปแบบเดียวกับตอนดู/โหลด ใช้ส่งให้ backend แนบในอีเมล
// pdfmake 0.3 คืน Promise และไม่รับ callback แล้ว ห้ามห่อด้วย new Promise ไม่งั้นค้างรอ resolve ที่ไม่มีวันมา
export const getPayslipPdfBase64 = async (node) => {
  const pdf = await buildPdfFromNode(node);
  return pdf.getBase64();
};

export default PayslipDocument;
